import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import { ThemeProvider } from "@/lib/theme/ThemeProvider";
import ReportsShell from "@/components/ReportsNav";
import Appearance from "@/pages/Appearance";
import { RecordingProvider } from "@/lib/recording";
import { ActiveClientProvider } from "@/lib/activeClient";
import ProtectedRoute from "@/components/ProtectedRoute";
import ErrorBoundary from "@/components/ErrorBoundary";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import ChangePassword from "@/pages/ChangePassword";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Users from "@/pages/Users";
import IntakeForms from "@/pages/IntakeForms";
import PublicIntakeForm from "@/pages/PublicIntakeForm";
import PublicCheckin from "@/pages/PublicCheckin";
import PublicCheckinSummary from "@/pages/PublicCheckinSummary";
import PublicCheckinManage from "@/pages/PublicCheckinManage";
import PublicProgressReviewDemo from "@/pages/PublicProgressReviewDemo";
import ProgressReviewPreview from "@/pages/ProgressReviewPreview";
import Checkins from "@/pages/Checkins";
import Dashboard from "@/pages/Dashboard";
import Appointments from "@/pages/Appointments";
import Orders from "@/pages/Orders";
import Products from "@/pages/Products";
import Payslips from "@/pages/Payslips";
import Receipts from "@/pages/Receipts";
import Reports from "@/pages/Reports";
import Schedule from "@/pages/Schedule";
import Settings from "@/pages/Settings";
import PrefillCoverage from "@/pages/PrefillCoverage";
import Messaging from "@/pages/Messaging";
import Reconciliation from "@/pages/Reconciliation";
import Clients from "@/pages/Clients";
import ClientDetail from "@/pages/ClientDetail";
import ClientDetailById from "@/pages/ClientDetailById";
import Letters from "@/pages/Letters";
import WritingDocumentation from "@/pages/WritingDocumentation";
import LetterCompose from "@/pages/LetterCompose";
import DocumentImports from "@/pages/DocumentImports";
import BulkProgramImport from "@/pages/BulkProgramImport";
import BlockLibrary from "@/pages/BlockLibrary";
import DeletedItems from "@/pages/DeletedItems";
import GoogleReviews from "@/pages/GoogleReviews";
import GpDirectory from "@/pages/GpDirectory";
import AssessmentRecovery from "@/pages/AssessmentRecovery";
import SbnReview from "@/pages/SbnReview";
import SbnClientDetail from "@/pages/SbnClientDetail";
import Assessments from "@/pages/Assessments";
import AssessmentEditor from "@/pages/AssessmentEditor";
import ClinicalDocDashboard from "@/pages/ClinicalDocDashboard";
import GovernanceDashboard from "@/pages/GovernanceDashboard";
import KpiReports from "@/pages/KpiReports";
import Tasks from "@/pages/Tasks";
import Procedures from "@/pages/Procedures";
import ProcedureDetail from "@/pages/ProcedureDetail";
import Learning from "@/pages/Learning";
import TrainingDetail from "@/pages/TrainingDetail";
import Notifications from "@/pages/Notifications";
import Enquiries from "@/pages/Enquiries";
import EnquiriesAnalytics from "@/pages/EnquiriesAnalytics";
import EnquiryInbox from "@/pages/EnquiryInbox";
import SocialInbox from "@/pages/SocialInbox";
import TeamDevelopment from "@/pages/TeamDevelopment";
import ReplyTemplatesManager from "@/pages/ReplyTemplatesManager";
import ResourceLibrary from "@/pages/ResourceLibrary";
import ResourceDetail from "@/pages/ResourceDetail";
import PublicResourceLink from "@/pages/PublicResourceLink";

const STAFF_FORBIDDEN_ROLES = ["owner", "manager"];

// Staff users have no Dashboard (and no Reports) — send them to their Schedule instead.
function HomeRoute() {
  const { user } = useAuth();
  if (user?.role === "staff") return <Navigate to="/schedule" replace />;
  return <Dashboard />;
}

// /receipts is a compatibility shim — the Receipts UI now lives on the
// Reconciliation page under the "New receipt" tab. Preserve every query
// param (source=, id=, regen=) and force tab=new so the receipt form
// opens pre-filled instead of landing on the default "Receipts journal".
// Bare /receipts (no query) keeps the ledger default so users who
// bookmarked the old URL don't have their expectations changed.
function ReceiptsRedirect() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const hasReceiptContext =
    params.get("regen") || params.get("source") || params.get("id");
  if (hasReceiptContext && !params.get("tab")) params.set("tab", "new");
  const qs = params.toString();
  return <Navigate to={qs ? `/reconciliation?${qs}` : "/reconciliation"} replace />;
}


// When the Google Drive OAuth popup lands back in the app (Brave/Chrome
// sometimes reuses the parent tab or renders the callback inside the
// popup with the whole app shell), surface any `?gdrive_error=` /
// `?gdrive_connected=1` as a toast so the user actually sees what
// happened, then strip the param from the URL.
function GDriveRedirectHandler() {
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const err = params.get("gdrive_error");
    const ok = params.get("gdrive_connected");
    if (!err && !ok) return;
    if (err) {
      toast.error("Google Drive connect failed", {
        description: decodeURIComponent(err).slice(0, 240),
      });
    } else {
      toast.success("Google Drive connected");
    }
    // Strip the param so a refresh doesn't re-toast, and post to opener
    // so any picker dialog polling for status can react immediately.
    try {
      if (window.opener && window.opener !== window) {
        window.opener.postMessage({ type: "gdrive_oauth", error: err, ok: !!ok }, "*");
        window.close();
        return;
      }
    } catch { /* ignore cross-origin */ }
    params.delete("gdrive_error");
    params.delete("gdrive_connected");
    const qs = params.toString();
    window.history.replaceState({}, "", location.pathname + (qs ? `?${qs}` : ""));
  }, [location]);
  return null;
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <ThemeProvider>
          <RecordingProvider>
          <ActiveClientProvider>
          <GDriveRedirectHandler />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/intake/:token" element={<PublicIntakeForm />} />
            <Route path="/exercise-progress-review/demo" element={<PublicProgressReviewDemo />} />
            <Route path="/exercise-progress-review/summary/:token" element={<PublicCheckinSummary />} />
            <Route path="/exercise-progress-review/manage/:token" element={<PublicCheckinManage />} />
            <Route path="/exercise-progress-review/:token" element={<PublicCheckin />} />
            <Route path="/r/:token" element={<PublicResourceLink />} />
            <Route path="/progress-review/preview/:vid" element={<ProtectedRoute><ProgressReviewPreview /></ProtectedRoute>} />
            <Route path="/change-password" element={<ProtectedRoute><ChangePassword /></ProtectedRoute>} />
            <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
              <Route path="/" element={<HomeRoute />} />
              {/* Reports hub — every report page shares the Reports tab bar. Owner + manager only. */}
              <Route element={<ProtectedRoute roles={STAFF_FORBIDDEN_ROLES}><ReportsShell /></ProtectedRoute>}>
                <Route path="/reports" element={<Reports />} />
                <Route path="/kpi" element={<KpiReports />} />
                <Route path="/appointments" element={<Appointments />} />
                <Route path="/orders" element={<Orders />} />
                <Route path="/products" element={<Products />} />
                <Route path="/payslips" element={<Payslips />} />
              </Route>
              <Route
                path="/tasks"
                element={<ProtectedRoute><Tasks /></ProtectedRoute>}
              />
              <Route
                path="/procedures"
                element={<ProtectedRoute><Procedures /></ProtectedRoute>}
              />
              <Route
                path="/procedures/:id"
                element={<ProtectedRoute><ProcedureDetail /></ProtectedRoute>}
              />
              <Route
                path="/learning"
                element={<ProtectedRoute><Learning /></ProtectedRoute>}
              />
              <Route
                path="/learning/training/:id"
                element={<ProtectedRoute><TrainingDetail /></ProtectedRoute>}
              />
              <Route
                path="/learning/team-development"
                element={<ProtectedRoute><TeamDevelopment /></ProtectedRoute>}
              />
              <Route
                path="/notifications"
                element={<ProtectedRoute><Notifications /></ProtectedRoute>}
              />
              <Route
                path="/enquiries"
                element={<ProtectedRoute><Enquiries /></ProtectedRoute>}
              />
              <Route
                path="/enquiries/analytics"
                element={<ProtectedRoute><EnquiriesAnalytics /></ProtectedRoute>}
              />
              <Route
                path="/enquiries/inbox"
                element={<ProtectedRoute><EnquiryInbox /></ProtectedRoute>}
              />
              <Route
                path="/enquiries/social"
                element={<ProtectedRoute><SocialInbox /></ProtectedRoute>}
              />
              <Route
                path="/settings/reply-templates"
                element={<ProtectedRoute><ReplyTemplatesManager /></ProtectedRoute>}
              />
              <Route path="/schedule" element={<Schedule />} />
              <Route
                path="/clients"
                element={<ProtectedRoute><Clients /></ProtectedRoute>}
              />
              <Route
                path="/clients/id/:clientId"
                element={<ProtectedRoute><ErrorBoundary><ClientDetailById /></ErrorBoundary></ProtectedRoute>}
              />
              <Route
                path="/clients/:emailEncoded"
                element={<ProtectedRoute><ErrorBoundary><ClientDetail /></ErrorBoundary></ProtectedRoute>}
              />
              <Route
                path="/receipts"
                element={<ReceiptsRedirect />}
              />
              <Route
                path="/intake-forms"
                element={<ProtectedRoute><IntakeForms /></ProtectedRoute>}
              />
              <Route
                path="/document-imports"
                element={<ProtectedRoute><DocumentImports /></ProtectedRoute>}
              />
              <Route
                path="/bulk-program-import"
                element={<ProtectedRoute><BulkProgramImport /></ProtectedRoute>}
              />
              <Route
                path="/program-library"
                element={<ProtectedRoute><BlockLibrary /></ProtectedRoute>}
              />
              {/* Legacy alias — kept so existing bookmarks + deep
                  links to the block library still work after the
                  page was folded into Program Library. */}
              <Route
                path="/program-blocks"
                element={<ProtectedRoute><BlockLibrary /></ProtectedRoute>}
              />
              <Route
                path="/assessments"
                element={<ProtectedRoute><Assessments /></ProtectedRoute>}
              />
              <Route path="/progress-review" element={<ProtectedRoute><Checkins /></ProtectedRoute>} />
              <Route path="/resource-library" element={<ProtectedRoute><ResourceLibrary /></ProtectedRoute>} />
              <Route path="/resource-library/:id" element={<ProtectedRoute><ResourceDetail /></ProtectedRoute>} />
              <Route
                path="/assessments/:id"
                element={<ProtectedRoute><AssessmentEditor /></ProtectedRoute>}
              />
              <Route
                path="/messaging"
                element={<ProtectedRoute roles={STAFF_FORBIDDEN_ROLES}><Messaging /></ProtectedRoute>}
              />
              <Route
                path="/reconciliation"
                element={<ProtectedRoute><Reconciliation /></ProtectedRoute>}
              />
              <Route
                path="/letters"
                element={<ProtectedRoute><Letters /></ProtectedRoute>}
              />
              <Route
                path="/writing"
                element={<ProtectedRoute><WritingDocumentation /></ProtectedRoute>}
              />
              <Route
                path="/letters/new"
                element={<ProtectedRoute><LetterCompose /></ProtectedRoute>}
              />
              <Route
                path="/letters/:letterId"
                element={<ProtectedRoute><LetterCompose /></ProtectedRoute>}
              />
              <Route
                path="/letters/gps"
                element={<ProtectedRoute><GpDirectory /></ProtectedRoute>}
              />
              <Route
                path="/assessments/recovery"
                element={<ProtectedRoute><AssessmentRecovery /></ProtectedRoute>}
              />
              <Route
                path="/reports/clinical-docs"
                element={<ProtectedRoute><ClinicalDocDashboard /></ProtectedRoute>}
              />
              <Route
                path="/governance"
                element={<ProtectedRoute roles={STAFF_FORBIDDEN_ROLES}><GovernanceDashboard /></ProtectedRoute>}
              />
              <Route
                path="/deleted-items"
                element={<ProtectedRoute roles={STAFF_FORBIDDEN_ROLES}><DeletedItems /></ProtectedRoute>}
              />
              <Route
                path="/sbn"
                element={<ProtectedRoute><SbnReview /></ProtectedRoute>}
              />
              <Route
                path="/sbn/clients/:clientId"
                element={<ProtectedRoute><SbnClientDetail /></ProtectedRoute>}
              />
              <Route path="/appearance" element={<Appearance />} />
              <Route
                path="/settings"
                element={<ProtectedRoute roles={STAFF_FORBIDDEN_ROLES}><Settings /></ProtectedRoute>}
              />
              <Route
                path="/admin/prefill-coverage"
                element={<ProtectedRoute roles={["owner","manager"]}><PrefillCoverage /></ProtectedRoute>}
              />
              <Route
                path="/users"
                element={<ProtectedRoute roles={["owner"]}><Users /></ProtectedRoute>}
              />
              <Route
                path="/reviews"
                element={<ProtectedRoute roles={STAFF_FORBIDDEN_ROLES}><GoogleReviews /></ProtectedRoute>}
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
          </ActiveClientProvider>
          </RecordingProvider>
          </ThemeProvider>
        </AuthProvider>
      </BrowserRouter>
      <Toaster richColors position="bottom-right" />
    </div>
  );
}
